/**
 * Split a long text into pages of at most `max` characters — without ever
 * cutting a sentence in half.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * The box used to stop dead at 5,000 characters: `maxLength` on the textarea,
 * a paste silently truncated, and no way to find out what had been dropped. The
 * limit itself is real — it is what one request to the model is allowed to
 * carry — but a limit is not the same thing as a refusal, and every serious
 * translator turns the second one into paging.
 *
 * ── Why it is plain code and not a model call ────────────────────────────────
 *
 * Because "where does this sentence end" is a question about punctuation and
 * whitespace, both of which are right there in the string. Asking a model to
 * segment a 40,000-character document would cost more than translating one page
 * of it, and would have to be re-run on every keystroke that shifts a boundary.
 * This runs in under a millisecond and is deterministic — the same text always
 * splits the same way, which is what makes a page number mean anything.
 *
 * ── The rule ─────────────────────────────────────────────────────────────────
 *
 * For each page, look at the next `max` characters and cut at the LAST clean
 * boundary in that window, trying four kinds in order of how much they mean:
 *
 *   1. a paragraph break — a blank line. The best possible cut: it is where the
 *      author themselves said one thought ended.
 *   2. the end of a sentence — `.`, `!`, `?`, `…`, `。`, `！`, `？` followed by
 *      space, with the guards below so `Mr. Smith` and `e.g. this` are not
 *      mistaken for one.
 *   3. a single line break — verse, addresses, subtitle files, anything written
 *      in lines rather than paragraphs.
 *   4. a space, so at worst a WORD survives whole.
 *
 * Each candidate has to fall in the last 45% of the window; a paragraph break
 * 200 characters in is a worse cut than a sentence end at 4,800, because it
 * would leave a nearly empty page and push the rest of the document along. Only
 * if none of the four qualifies — one 5,000-character "word", a wall of CJK
 * with no punctuation at all — does it cut at exactly `max`.
 *
 * ── The invariant everything else leans on ───────────────────────────────────
 *
 * `pages.map(p => p.text).join('') === doc`, exactly. Boundaries are indices
 * into the original string and nothing is dropped, added or trimmed, so editing
 * page *n* is a splice of `doc` between two offsets — which is what lets the
 * translator keep one document in state and hand each page to the model in
 * turn.
 */

export interface Page {
  /** Offset in the document where this page starts. */
  start: number;
  /** Offset one past its last character. */
  end: number;
  text: string;
}

/**
 * How far into the window a boundary has to be to be taken.
 *
 * Below this it is cheaper to look for a lesser boundary further on than to
 * accept a half-empty page — a document of 20 short paragraphs would otherwise
 * come out as 20 pages of 300 characters each.
 */
const MIN_FILL = 0.55;

/** Blank line, plus any whitespace that follows it, so the next page starts clean. */
const PARAGRAPH = /\n[ \t\r]*\n[\s]*/g;
/**
 * A sentence ender, its closing quotes and brackets, and the space after it.
 *
 * Two alternatives, because the space is not optional in one script and does not
 * exist in the other. Latin punctuation is only a sentence end when whitespace
 * follows — otherwise `3.5` and `google.com` would be boundaries. Full-width
 * CJK punctuation carries its own spacing and is normally written with nothing
 * after it at all, so requiring whitespace there found no boundaries whatsoever
 * and paginated Chinese by cutting it mid-clause.
 */
const SENTENCE = /[.!?…؟।][)\]"'”’»』」]*[ \t\r\n]+|[。！？．…][)\]"'”’»』」]*[ \t\r\n]*/g;
/** Any line break. */
const LINE = /\n[ \t\r]*/g;
/** Last resort before a hard cut: a word boundary. */
const SPACE = /[ \t]+/g;

/**
 * Words that end in a full stop without ending a sentence.
 *
 * Not an attempt at a complete list — there is no complete list — but at the
 * handful common enough to hit a real document, plus the general rules below,
 * which catch far more than any list could: an initial (`J. R. R.`), and a
 * sentence that would start with a lowercase letter, which real ones do not.
 */
const ABBREVIATION =
  /(?:^|[\s("'([])(?:mr|mrs|ms|dr|prof|rev|hon|st|mt|sr|jr|vs|etc|al|approx|dept|univ|inst|corp|inc|ltd|co|no|vol|fig|eq|ch|pp|ed|est|min|max|e\.g|i\.e|a\.m|p\.m|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec|mon|tue|wed|thu|fri|sat|sun)\.$/i;

/**
 * Is the full stop at `dot` really the end of a sentence?
 *
 * Applied only to `.` — the other terminators (`!`, `?`, `。`) do not appear in
 * the middle of words, so putting them through this would only ever produce
 * false negatives.
 */
function endsSentence(doc: string, dot: number, nextChar: string): boolean {
  if (doc[dot] !== '.') return true;

  // `J. R. R. Tolkien`, `A. Einstein` — a single capital before the stop.
  const before = doc.slice(Math.max(0, dot - 2), dot);
  if (/(^|[\s("'])[A-Z]$/.test(before)) return false;

  if (ABBREVIATION.test(doc.slice(Math.max(0, dot - 12), dot + 1))) return false;

  // Sentences do not begin in lowercase. This one rule covers every abbreviation
  // the list above misses, in every Latin-script language, at no cost to CJK or
  // Korean — neither has a lowercase to test.
  if (/[a-zà-öø-ÿ]/.test(nextChar)) return false;

  return true;
}

/**
 * The end offset of the last match of `re` that lies inside the window and is
 * far enough in to be worth taking. Null when there is none.
 *
 * `accept` gets the index of the matched terminator and the character that
 * would start the next page, and is where the sentence guards live.
 */
function lastBoundary(
  doc: string,
  start: number,
  limit: number,
  floor: number,
  re: RegExp,
  accept?: (terminator: number, nextChar: string) => boolean,
): number | null {
  const window = doc.slice(start, limit);
  let best: number | null = null;
  let m: RegExpExecArray | null;
  re.lastIndex = 0;

  while ((m = re.exec(window)) !== null) {
    // Where the next page would begin: after the whitespace this match consumed.
    const cut = start + m.index + m[0].length;
    if (m.index === re.lastIndex) re.lastIndex++; // zero-length match guard
    if (cut > limit) break;
    if (cut < floor) continue;
    if (accept && !accept(start + m.index, doc[cut] ?? '')) continue;
    best = cut;
  }
  return best;
}

export function paginate(doc: string, max: number): Page[] {
  const page = (start: number, end: number): Page => ({ start, end, text: doc.slice(start, end) });

  // One empty page rather than none: the caller always renders `pages[n]`, and a
  // box with nothing in it is still a box.
  if (!doc) return [page(0, 0)];
  if (max < 1) return [page(0, doc.length)];

  const pages: Page[] = [];
  let start = 0;

  while (start < doc.length) {
    if (doc.length - start <= max) {
      pages.push(page(start, doc.length));
      break;
    }

    const limit = start + max;
    const floor = start + Math.floor(max * MIN_FILL);

    const cut =
      lastBoundary(doc, start, limit, floor, PARAGRAPH) ??
      lastBoundary(doc, start, limit, floor, SENTENCE, (t, next) => endsSentence(doc, t, next)) ??
      lastBoundary(doc, start, limit, floor, LINE) ??
      lastBoundary(doc, start, limit, floor, SPACE) ??
      limit;

    pages.push(page(start, cut));
    start = cut;
  }

  return pages;
}

/** Which page an offset in the document falls on. Clamped, never -1. */
export function pageOfOffset(pages: Page[], offset: number): number {
  for (let i = 0; i < pages.length; i++) {
    // `<= end` so a caret sitting exactly on a boundary stays on the page it was
    // typed into rather than jumping ahead of the space it just added.
    if (offset <= pages[i].end) return i;
  }
  return pages.length - 1;
}

/**
 * The page numbers to draw, with gaps as `null`.
 *
 * A 40-page document cannot show 40 buttons on a phone, and "…" in the middle
 * is the shape every pager in the world uses, so it needs no explaining: first,
 * last, and a window around where you are.
 */
export function pageWindow(count: number, current: number, around = 1): (number | null)[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i);

  const keep = new Set<number>([0, count - 1, current]);
  for (let d = 1; d <= around; d++) {
    keep.add(Math.max(0, current - d));
    keep.add(Math.min(count - 1, current + d));
  }
  // The two ends always show two numbers, so the row does not change width every
  // time the current page moves next to the first or the last.
  if (current <= 2) { keep.add(1); keep.add(2); }
  if (current >= count - 3) { keep.add(count - 2); keep.add(count - 3); }

  const sorted = [...keep].filter((n) => n >= 0 && n < count).sort((a, b) => a - b);
  const out: (number | null)[] = [];
  let prev = -1;
  for (const n of sorted) {
    // A gap of exactly one page is drawn as the page itself: "…" standing in for
    // a single number is longer than the number and tells you less.
    if (prev >= 0 && n - prev === 2) out.push(prev + 1);
    else if (prev >= 0 && n - prev > 2) out.push(null);
    out.push(n);
    prev = n;
  }
  return out;
}
